import { baseColors } from "./base";

/**
 * Creates a semantic token map for a theme
 */
export interface SemanticTokens {
  // Background colors
  background: {
    primary: string; // Main background
    secondary: string; // Card backgrounds, elevated surfaces
    tertiary: string; // Subtle backgrounds
    elevated: string; // Modals, dialogs, popovers
    hover: string; // Hover states
    selected: string; // Selected items
    disabled: string; // Disabled elements
    overlay: string; // Overlays, masks
    whiteBb: string; // White background with border
    secondaryBg: string; // Secondary background,
    new:string
  };

  // Foreground/Text colors
  text: {
    primary: string; // Main text
    secondary: string; // Secondary text, descriptions
    tertiary: string; // Muted text, placeholders
    disabled: string; // Disabled text
    inverse: string; // Text on dark backgrounds
    link: string; // Links
    success: string; // Success messages
    warning: string; // Warning messages
    alert: string; // Alert messages
    error: string; // Error messages
    info: string; // Info messages
    label: string; // label messages
    grey25: string; // grey25 messages
  };

  // Border colors
  border: {
    primary: string; // Default borders
    secondary: string; // Subtle borders
    focus: string; // Focused elements
    error: string; // Error state borders
    disabled: string; // Disabled borders
    warningBorder: string;
    alert: string;
  };

  // Brand colors
  brand: {
    primary: string; // Main brand color
    primaryHover: string; // Primary hover state
    primaryActive: string; // Primary active state
    secondary: string; // Secondary brand color
    secondaryHover: string;
    secondaryActive: string;
    warningBorder: string;
  };

  // Semantic status colors
  status: {
    success: string;
    successBg: string;
    successHover: string;
    successActive: string;
    warning: string;
    warningBg: string;
    warningHover: string;
    warningActive: string;
    error: string;
    errorBg: string;
    errorHover: string;
    errorActive: string;
    info: string;
    infoBg: string;
    infoHover: string;
    infoActive: string;
  };

  // Component-specific colors
  components: {
    // Buttons
    button: {
      primaryBg: string;
      primaryText: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryText: string;
      secondaryHover: string;
      secondaryDisabledBg: string;
      disabledBg: string;
      disabledText: string;
      dimmedDark: string;
      dimmedLight: string;
      secondaryMain: string;
      secondaryTextDark: string;
      secondaryTabHover: string;
      backbuttonHover: string;
      backbuttonClick: string;
      backbuttonBorder: string;
    };
    divider: {
      base: string;
    };

    // Inputs
    input: {
      background: string;
      border: string;
      borderHover: string;
      borderFocus: string;
      text: string;
      placeholder: string;
      disabled: string;
    };

    // Cards
    card: {
      background: string;
      border: string;
      shadow: string;
      headerBg: string;
      headerText: string;
    };

    // Navigation
    navigation: {
      background: string;
      text: string;
      textHover: string;
      active: string;
      activeBg: string;
      border: string;
    };

    // Tables
    table: {
      headerBg: string;
      headerText: string;
      rowBg: string;
      rowBgAlt: string;
      rowHover: string;
      border: string;
      selectedBg: string;
      tableOccupiedBg: string;
    };

    // Modals/Dialogs
    dialog: {
      background: string;
      overlay: string;
      border: string;
    };

    // Checkboxes & Radios
    checkbox: {
      background: string;
      border: string;
      checked: string;
      disabled: string;
    };

    // Tabs
    tab: {
      background: string;
      text: string;
      activeBackground: string;
      activeText: string;
      border: string;
    };

    // Drawer/Sidebar
    drawer: {
      background: string;
      text: string;
      border: string;
    };

    // Icons
    icon: {
      color: string;
      hover: string;
      active: string;
      disabled: string;
      secondaryColor: string;
      secondaryIcon: string;
    };
    badge: {
      background: string;
      successBg: string;
      warningBg: string;
      errorBg: string;
      infoBg: string;
    };
  };

  // Special purpose colors
  special: {
    overlay: string;
    divider: string;
    shadow: string;
    focus: string;
    transparent: string;
  };
} 

type ThemeMode = "light" | "dark";
const isLight = (mode: ThemeMode) => mode === "light";

const createSemanticTokens = (mode: ThemeMode): SemanticTokens => {
  const B = baseColors;
  return {
    background: {
      primary: B.base.background[mode],
      secondary: B.base.whiteBg[mode],
      tertiary: B.base.secondBg[mode],
      elevated: B.base.whiteBg[mode],
      hover: B.primary.soft[mode],
      selected: B.primary.soft[mode],
      disabled: isLight(mode)
        ? B.button.dimmedLight[mode]
        : B.button.dimmedDark[mode],
      whiteBb: B.text.light100[mode],
      overlay: isLight(mode) ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
      secondaryBg: B.base.secondBg[mode],
      new :B.base.new[mode]
    },

    text: {
      primary: B.text.black100[mode],
      secondary: B.text.grey50[mode],
      tertiary: B.text.grey25[mode],
      disabled: B.text.grey25[mode],
      inverse: B.text.light100[mode],
      link: B.primary.main[mode],
      success: B.alert.success.text[mode],
      warning: B.alert.warning.text[mode],
      alert: B.alert.warning.text[mode],
      error: B.alert.error.text[mode],
      info: B.alert.info.text[mode],
      label: B.secondary.darkOrange[mode],
      grey25: B.text.grey25[mode],
    },

    border: {
      primary: B.base.inputBorder[mode],
      secondary: B.base.divider[mode],
      focus: B.primary.main[mode],
      error: B.alert.error.border[mode],
      disabled: B.base.divider[mode],
      warningBorder: B.alert.warning.border[mode],
      alert: B.alert.error.border[mode],
    },

    brand: {
      primary: B.primary.main[mode],
      primaryHover: B.primary.dark[mode],
      primaryActive: B.primary.dark[mode],
      secondary: B.primary.soft[mode],
      secondaryHover: B.primary.softHover[mode],
      secondaryActive: B.primary.softHover[mode],
      warningBorder: B.alert.warning.border[mode],
    },

    status: {
      success: B.alert.success.text[mode],
      successBg: B.alert.success.lightBg[mode],
      successHover: B.alert.success.darkBg[mode],
      successActive: B.alert.success.darkBg[mode],
      warning: B.alert.warning.text[mode],
      warningBg: B.alert.warning.lightBg[mode],
      warningHover: B.alert.warning.darkBg[mode],
      warningActive: B.alert.warning.darkBg[mode],
      error: B.alert.error.text[mode],
      errorBg: B.alert.error.lightBg[mode],
      errorHover: B.alert.error.darkBg[mode],
      errorActive: B.alert.error.darkBg[mode],
      info: B.alert.info.text[mode],
      infoBg: B.alert.info.lightBg[mode],
      infoHover: B.alert.info.darkBg[mode],
      infoActive: B.alert.info.darkBg[mode],
    },

    components: {
      button: {
        primaryBg: B.primary.main[mode],
        primaryText: B.text.light100[mode],
        primaryHover: B.primary.dark[mode],
        secondaryBg: B.base.whiteBg[mode],
        secondaryText: isLight(mode)
          ? B.primary.dark[mode]
          : B.primary.main[mode],
        secondaryHover: B.primary.soft[mode],
        secondaryDisabledBg: B.button.secondaryDimmed[mode],
        disabledBg: isLight(mode)
          ? B.primary.mainDimmed[mode]
          : B.button.dimmedDark[mode],
        disabledText: B.text.grey25[mode],
        dimmedDark: B.button.dimmedDark[mode],
        dimmedLight: B.button.dimmedLight[mode],
        secondaryMain: B.secondary.mainOrange[mode],
        secondaryTextDark: B.secondary.textDark[mode],
        secondaryTabHover: B.secondary.buttonTabHover[mode],
        backbuttonHover: B.base.secondBg[mode],
        backbuttonClick: B.primary.softHover[mode],
        backbuttonBorder: B.base.inputBorder[mode],
      },
      divider: { base: B.base.divider[mode] },

      input: {
        background: B.base.whiteBg[mode],
        border: B.base.inputBorder[mode],
        borderHover: B.text.grey50[mode],
        borderFocus: B.primary.main[mode],
        text: B.text.black100[mode],
        placeholder: B.text.grey50[mode],
        disabled: isLight(mode)
          ? B.button.dimmedLight[mode]
          : B.button.dimmedDark[mode],
      },

      card: {
        background: B.base.whiteBg[mode],
        border: B.base.divider[mode],
        shadow: isLight(mode)
          ? "0px 0px 20px 0px rgba(0, 0, 0, 0.08)"
          : "0px 0px 20px 0px rgba(0, 0, 0, 0.3)",
        headerBg: B.base.background[mode],
        headerText: B.text.black100[mode],
      },

      navigation: {
        background: B.base.whiteBg[mode],
        text: B.text.black100[mode],
        textHover: B.text.grey50[mode],
        active: B.primary.main[mode],
        activeBg: B.primary.soft[mode],
        border: B.base.divider[mode],
      },

      table: {
        headerBg: B.base.background[mode],
        headerText: B.text.black100[mode],
        rowBg: B.base.whiteBg[mode],
        rowBgAlt: B.base.background[mode],
        rowHover: B.primary.soft[mode],
        border: B.base.divider[mode],
        selectedBg: B.alert.warning.lightBg[mode],
        tableOccupiedBg: B.secondary.darkOrange[mode],
      },

      dialog: {
        background: B.base.whiteBg[mode],
        overlay: isLight(mode) ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
        border: B.base.divider[mode],
      },

      checkbox: {
        background: B.primary.lightBtnBorder[mode],
        border: B.base.inputBorder[mode],
        checked: B.primary.main[mode],
        disabled: isLight(mode)
          ? B.button.dimmedLight[mode]
          : B.button.dimmedDark[mode],
      },

      tab: {
        background: "transparent",
        text: B.text.black100[mode],
        activeBackground: B.primary.soft[mode],
        activeText: B.text.black100[mode],
        border: B.base.divider[mode],
      },

      drawer: {
        background: B.base.whiteBg[mode],
        text: B.text.black100[mode],
        border: B.base.divider[mode],
      },

      icon: {
        color: B.base.icon[mode],
        secondaryColor: B.base.icon[mode],
        secondaryIcon: B.base.icon[mode],
        hover: B.primary.main[mode],
        active: isLight(mode) ? B.primary.dark[mode] : B.primary.main[mode],
        disabled: isLight(mode)
          ? B.button.dimmedLight[mode]
          : B.text.grey25[mode],
      },
      badge: {
        background: B.secondary.darkOrange[mode],
        warningBg: B.alert.warning.lightBg[mode],
        errorBg: B.alert.error.lightBg[mode],
        successBg: B.alert.success.lightBg[mode],
        infoBg: B.alert.info.lightBg[mode],
      },
    },

    special: {
      overlay: isLight(mode) ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
      divider: B.base.divider[mode],
      shadow: isLight(mode) ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.3)",
      focus: B.primary.main[mode],
      transparent: "transparent",
    },
  }; 
};

export const lightSemanticTokens: SemanticTokens = createSemanticTokens("light");
export const darkSemanticTokens: SemanticTokens = createSemanticTokens("dark");
